import { fetchPublicVersions, selectHighestVersion } from '../services/releaseApi';
import { OPTIONS } from "./app-config";
import { getThemeColors } from './theme';

const upsertLink = (rel, href, attributes = {}) => {
    if (!href) return;
    let link = document.head.querySelector(`link[rel="${rel}"]`);
    if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
    }
    link.href = href;
    Object.entries(attributes).forEach(([name, value]) => link.setAttribute(name, value));
};

const upsertMeta = (name, content) => {
    if (!content) return;
    let meta = document.head.querySelector(`meta[name="${name}"]`);
    if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
    }
    meta.content = content;
};

export function applyColorScheme(config, mode = 'light') {
    if (!config?.valid) return;
    const palette = getThemeColors(config, mode);
    const root = document.documentElement;

    root.dataset.oem = config.oem;
    root.dataset.environment = config.environment;
    root.dataset.colorScheme = palette.mode;

    // Generated article content uses the same resolved colors as MUI components.
    if (palette.progress){
        root.style.setProperty('--brand-progress-bar', palette.progress);
    }
    root.style.setProperty('--brand-accent', palette.accent);

    // Apply primary, secondary, background, paper, and text colors
    root.style.setProperty('--brand-primary', palette.primary);
    root.style.setProperty('--brand-secondary', palette.secondary);
    root.style.setProperty('--brand-background', palette.background);
    root.style.setProperty('--brand-paper', palette.paper);
    root.style.setProperty('--brand-text', palette.text);
    root.style.setProperty('--brand-heading-font', config.fonts.heading);
    root.style.setProperty('--brand-body-font', config.fonts.body);
    root.style.setProperty('--brand-code-font', config.fonts.code);
    upsertMeta('theme-color', palette.primary);
}

export function applyRuntimeBranding(config) {
    if (!config?.valid) {
        document.title = config?.error?.title || 'Configuration error';
        return;
    }

    document.title = config.metadata.title;
    applyColorScheme(config, 'light');

    upsertMeta('description', config.metadata.description);
    upsertMeta('theme-color', config.palette.light.primary);
    upsertLink('icon', config.icon.favicon);
    upsertLink('apple-touch-icon', config.icon.appleTouchIcon);
    upsertLink('manifest', config.metadata.manifest);

    (config.fonts.stylesheets || []).forEach((href) => {
        if (document.head.querySelector(`link[data-oem-font="${href}"]`)) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.dataset.oemFont = href;
        document.head.appendChild(link);
    });

    if (config.analytics?.enabled && config.analytics.scriptUrl) {
        const script = document.createElement('script');
        script.src = config.analytics.scriptUrl;
        script.async = true;
        script.dataset.oemAnalytics = config.oem;
        document.head.appendChild(script);
    }
}

export function needsVersionResolution(config) {
    if (!config?.valid || window.location.pathname !== '/') return false;
    return !new URL(window.location.href).searchParams.get('ver');
}

export async function ensureDefaultRoute(
    config,
    loadVersions = fetchPublicVersions
) {
    if (!config?.valid || window.location.pathname !== '/') return false;

    const url = new URL(window.location.href);
    const defaults = {
        lang: config.defaults.language,
        product: config.defaults.product,
    };

    if (!url.searchParams.get('ver')) {
        const publicVersions = await loadVersions();
        const highestVersion = selectHighestVersion(publicVersions);
        if (!highestVersion) {
            throw new Error('The backend returned no public documentation versions.');
        }
        defaults.ver = highestVersion.code;

        // Set preferred language if available
        if (highestVersion.languages.includes(OPTIONS.preferredLanguage)) {
            defaults.lang = OPTIONS.preferredLanguage;
        }
    }

    let changed = false;
    Object.entries(defaults).forEach(([key, value]) => {
        if (!url.searchParams.get(key) && value) {
            url.searchParams.set(key, value);
            changed = true;
        }
    });

    if (changed) {
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    return changed;
}
